import React, { useEffect, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Typography from '@tiptap/extension-typography';
import Link from '@tiptap/extension-link';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableHeader from '@tiptap/extension-table-header';
import TableCell from '@tiptap/extension-table-cell';
import VoiceRecorderButton from './VoiceRecorderButton';
import AskNeuButton from './AskNeuButton';
import AskNeuTextButton from './AskNeuTextButton';
import TableButton from './TableButton';

interface RichTextEditorProps {
  content: string;
  onChange: (content: string) => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  placeholder?: string;
  className?: string;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  content,
  onChange,
  onKeyDown,
  placeholder = "Start writing your note...",
  className = ""
}) => {
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
        bulletList: {
          keepMarks: true,
          keepAttributes: false,
        },
        orderedList: {
          keepMarks: true,
          keepAttributes: false,
        },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-blue-600 underline hover:text-blue-800 cursor-pointer',
        },
      }),
      Placeholder.configure({
        placeholder,
      }),
      Typography,
      Table.configure({
        resizable: true,
      }),
      TableRow,
      TableHeader,
      TableCell,
    ],
    content,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange(html);
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm sm:prose lg:prose-lg xl:prose-2xl mx-auto focus:outline-none min-h-full p-4',
      },
      handleKeyDown: (view, event) => {
        if (onKeyDown) {
          onKeyDown(event as any);
        }
        return false;
      },
    },
  });

  // Update editor content when prop changes (for external updates)
  useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content, false);
    }
  }, [content, editor]);

  const handleVoiceTranscription = (transcription: string) => {
    if (!editor) return;
    
    try {
      // Check if editor was focused before we try to focus it
      const wasAlreadyFocused = editor.isFocused;
      
      // Ensure editor is focused first
      if (!editor.isFocused) {
        editor.commands.focus();
      }
      
      // If editor wasn't focused initially, always insert as plain paragraph at the end
      if (!wasAlreadyFocused) {
        // Insert a new paragraph with the transcribed text
        editor.chain()
          .focus()
          .insertContent('<p>' + transcription + '</p>')
          .run();
      } else {
        // Get current cursor position after focusing
        const { from } = editor.state.selection;
        
        if (from === 0 || editor.isEmpty) {
          // If cursor is at the beginning or there's no content, just insert as paragraph
          editor.chain().focus().insertContent('<p>' + transcription + ' </p>').run();
        } else {
          // Insert at cursor position with a space before if needed (preserve current formatting)
          const textBefore = editor.state.doc.textBetween(Math.max(0, from - 1), from);
          const needsSpaceBefore = textBefore && textBefore !== ' ' && textBefore !== '\n';
          const textToInsert = (needsSpaceBefore ? ' ' : '') + transcription + ' ';
          
          editor.chain().focus().insertContent(textToInsert).run();
        }
      }
    } catch (error) {
      console.error('Error inserting voice transcription:', error);
      // Fallback: simple insertion
      editor.chain().focus().insertContent(transcription + ' ').run();
    }
  };

  const handleNeuResponse = (question: string, response: string) => {
    if (!editor) return;
    
    try {
      // Always insert Neu responses as a formatted block
      const neuBlock = `
<div style="border-left: 4px solid #7c3aed; background-color: #f3f4f6; padding: 12px; margin: 8px 0; border-radius: 4px;">
  <div style="font-weight: 600; color: #7c3aed; font-size: 14px; margin-bottom: 4px;">🤖 Ask Neu</div>
  <div style="font-style: italic; color: #6b7280; font-size: 13px; margin-bottom: 8px;">"${question}"</div>
  <div style="color: #374151; line-height: 1.5;">${response}</div>
</div>
<p></p>`;

      // Focus editor and insert the Neu response block
      editor.chain()
        .focus()
        .insertContent(neuBlock)
        .run();
        
    } catch (error) {
      console.error('Error inserting Neu response:', error);
      // Fallback: simple text insertion
      editor.chain()
        .focus()
        .insertContent(`\n\n🤖 Ask Neu: "${question}"\n\n${response}\n\n`)
        .run();
    }
  };

  const handleAddLink = () => {
    if (!editor) return;
    
    const { from, to } = editor.state.selection;
    const selectedText = editor.state.doc.textBetween(from, to);
    
    if (selectedText) {
      // If text is selected, show dialog to add URL
      setLinkUrl('');
      setShowLinkDialog(true);
    } else {
      // If no text selected, prompt for both text and URL
      let url = window.prompt('Enter URL:');
      const text = window.prompt('Enter link text:');
      
      if (url && text) {
        // Ensure URL has proper protocol
        url = normalizeUrl(url);
        editor.chain().focus().insertContent(`<a href="${url}">${text}</a>`).run();
      }
    }
  };

  const normalizeUrl = (url: string): string => {
    // Remove any leading/trailing whitespace
    url = url.trim();
    
    // If URL already has protocol, return as is
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    
    // If URL starts with www., add https://
    if (url.startsWith('www.')) {
      return `https://${url}`;
    }
    
    // If URL looks like a domain (contains a dot), add https://
    if (url.includes('.') && !url.includes(' ')) {
      return `https://${url}`;
    }
    
    // Otherwise, assume it needs https://
    return `https://${url}`;
  };

  const handleLinkSubmit = () => {
    if (!editor || !linkUrl) return;
    
    // Normalize the URL before setting the link
    const normalizedUrl = normalizeUrl(linkUrl);
    editor.chain().focus().setLink({ href: normalizedUrl }).run();
    setShowLinkDialog(false);
    setLinkUrl('');
  };

  const handleRemoveLink = () => {
    if (!editor) return;
    editor.chain().focus().unsetLink().run();
  };

  if (!editor) {
    return (
      <div className={`flex items-center justify-center h-32 ${className}`}>
        <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Toolbar - Fixed at top */}
      <div className="flex-shrink-0 border-b border-gray-200 p-2 bg-white sticky top-0 z-10">
        <div className="flex items-center gap-1 flex-wrap">
          <button
            onClick={() => editor?.chain().focus().toggleBold().run()}
            className={`p-2 rounded hover:bg-gray-100 ${
              editor?.isActive('bold') ? 'bg-gray-200' : ''
            }`}
            title="Bold (Ctrl+B)"
          >
            <strong>B</strong>
          </button>
          
          <button
            onClick={() => editor?.chain().focus().toggleItalic().run()}
            className={`p-2 rounded hover:bg-gray-100 ${
              editor?.isActive('italic') ? 'bg-gray-200' : ''
            }`}
            title="Italic (Ctrl+I)"
          >
            <em>I</em>
          </button>
          
          <button
            onClick={() => editor?.chain().focus().toggleStrike().run()}
            className={`p-2 rounded hover:bg-gray-100 ${
              editor?.isActive('strike') ? 'bg-gray-200' : ''
            }`}
            title="Strikethrough"
          >
            <s>S</s>
          </button>
          
          <div className="w-px h-6 bg-gray-300 mx-1" />
          
          <button
            onClick={() => editor?.chain().focus().toggleHeading({ level: 1 }).run()}
            className={`px-2 py-1 rounded hover:bg-gray-100 text-sm font-medium ${
              editor?.isActive('heading', { level: 1 }) ? 'bg-gray-200' : ''
            }`}
            title="Heading 1"
          >
            H1
          </button>
          
          <button
            onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()}
            className={`px-2 py-1 rounded hover:bg-gray-100 text-sm font-medium ${
              editor?.isActive('heading', { level: 2 }) ? 'bg-gray-200' : ''
            }`}
            title="Heading 2"
          >
            H2
          </button>
          
          <button
            onClick={() => editor?.chain().focus().toggleHeading({ level: 3 }).run()}
            className={`px-2 py-1 rounded hover:bg-gray-100 text-sm font-medium ${
              editor?.isActive('heading', { level: 3 }) ? 'bg-gray-200' : ''
            }`}
            title="Heading 3"
          >
            H3
          </button>
          
          <div className="w-px h-6 bg-gray-300 mx-1" />
          
          <button
            onClick={() => editor?.chain().focus().toggleBulletList().run()}
            className={`p-2 rounded hover:bg-gray-100 ${
              editor?.isActive('bulletList') ? 'bg-gray-200' : ''
            }`}
            title="Bullet List"
          >
            •
          </button>
          
          <button
            onClick={() => editor?.chain().focus().toggleOrderedList().run()}
            className={`p-2 rounded hover:bg-gray-100 ${
              editor?.isActive('orderedList') ? 'bg-gray-200' : ''
            }`}
            title="Numbered List"
          >
            1.
          </button>
          
          <button
            onClick={() => editor?.chain().focus().toggleBlockquote().run()}
            className={`p-2 rounded hover:bg-gray-100 ${
              editor?.isActive('blockquote') ? 'bg-gray-200' : ''
            }`}
            title="Quote"
          >
            "
          </button>
          
          <button
            onClick={() => editor?.chain().focus().toggleCodeBlock().run()}
            className={`p-2 rounded hover:bg-gray-100 ${
              editor?.isActive('codeBlock') ? 'bg-gray-200' : ''
            }`}
            title="Code Block"
          >
            &lt;/&gt;
          </button>
          
          <div className="w-px h-6 bg-gray-300 mx-1" />
          
          <button
            onClick={handleAddLink}
            className={`p-2 rounded hover:bg-gray-100 ${
              editor?.isActive('link') ? 'bg-gray-200' : ''
            }`}
            title="Add Link"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.102m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
            </svg>
          </button>
          
          <button
            onClick={() => editor?.chain().focus().unsetLink().run()}
            className="p-2 rounded hover:bg-gray-100"
            title="Remove Link"
            disabled={!editor?.isActive('link')}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          
          <div className="w-px h-6 bg-gray-300 mx-1" />
          
          {/* Voice Recording Button */}
          <VoiceRecorderButton 
            onTranscriptionComplete={handleVoiceTranscription}
            disabled={false}
          />
          
          {/* Ask Neu Button */}
          <AskNeuButton 
            onNeuResponse={handleNeuResponse}
            disabled={false}
          />
          
          {/* Ask Neu Text Button */}
          <AskNeuTextButton 
            onNeuResponse={handleNeuResponse}
            disabled={false}
          />
          
          {/* Table Button */}
          <TableButton 
            editor={editor}
            disabled={false}
          />
          
          <div className="w-px h-6 bg-gray-300 mx-1" />
        </div>
      </div>

      {/* Link Dialog - Also fixed */}
      {showLinkDialog && (
        <div className="flex-shrink-0 p-3 bg-blue-50 border-b border-blue-200 sticky top-16 z-10">
          <div className="flex items-center space-x-2">
            <input
              type="url"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              placeholder="Enter URL (e.g., https://example.com)"
              className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleLinkSubmit();
                } else if (e.key === 'Escape') {
                  setShowLinkDialog(false);
                  setLinkUrl('');
                }
              }}
            />
            <button
              onClick={handleLinkSubmit}
              disabled={!linkUrl.trim()}
              className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Add
            </button>
            <button
              onClick={() => {
                setShowLinkDialog(false);
                setLinkUrl('');
              }}
              className="px-3 py-1 bg-gray-300 text-gray-700 rounded text-sm hover:bg-gray-400"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Editor Content - Scrollable area */}
      <div className="flex-1 overflow-y-auto">
        <EditorContent 
          editor={editor} 
          className="prose prose-sm max-w-none p-4 focus:outline-none h-full"
        />
      </div>
    </div>
  );
};

export default RichTextEditor; 